import { BIRDIE_RESULT_HOLD_MS } from './boardPhase'

/**
 * The tee-off countdown (B37) — what fills the reveal delay.
 *
 * Owner, 2026-09-17: "on crash / footfall we have a counter that shows during
 * that delay, like 3, 2, 1, and then the round starts — i think we probably
 * need something on this game too as it feels like nothing happens once the
 * betting phase is over, just waiting".
 *
 * The gap is real: betting closes, `race_started` lands, and the engine plays
 * its post-start cutscene for `reveal_delay_s` before the putting clip begins.
 * The board is `locked` for all of it and the callout slot is empty, so the
 * count takes THAT slot and hands it to "Putt 1 of 3" rather than stacking a
 * second overlay over live video.
 *
 * NO FALLBACK DELAY, deliberately (owner's explicit instruction). The number is
 * the engine's — it publishes it on `race_ready.payload.odds.reveal_delay_s`
 * (`RoundOdds.revealDelayS`) and the backend's post-close bet cutoff is derived
 * from the same value. A widget-side 5.0 would be a second copy of it, and the
 * last time that number was duplicated the bet window closed before the widget
 * submitted and every bet was silently refused. A round that carries no delay
 * draws no countdown at all.
 */
/** A whole second left (3, 2, 1 today; up to X on a hard round), or the held `ready`. */
export type TeeOffBeat = number | 'ready'

export interface TeeOffInput {
  /**
   * `race_started`'s wire timestamp, ms. The engine's clock against the
   * browser's — the same pairing the betting ring's `drain` already uses, so
   * the two countdowns on this board read one another's skew, not two.
   */
  startedAtMs?: number
  /** `RoundOdds.revealDelayS`. Absent ⇒ no countdown (see above). */
  revealDelayS?: number
  nowMs: number
  /**
   * True once the board is DRAWING the round's first putt — `livePutts` on the
   * phase, which is the instant the putt callout claims the slot. NOT the raw
   * store entry: that one is still behind its own playout hold, and handing
   * over there would re-open the dead air by a second.
   */
  firstPuttSeen: boolean
  /** Settled or cancelled — there is no tee-off left to count to. */
  settled: boolean
  /**
   * How many whole seconds out the count starts. 3 by default; a hard round
   * with the short hold passes X, so the tee-off IS the lock: it appears as the
   * capsule slides to the corner and counts down to the cut (owner 10-29).
   */
  beats?: number
}

/** Beats, and therefore the first one: three seconds out. */
const TEE_OFF_BEATS = 3

/**
 * How long the quiet `ready` beat may stand past the window.
 *
 * The event clock LEADS the viewer's frames by their playout buffer, so a count
 * that ran to zero on the event clock would strand the player on "0" — the very
 * dead air this removes. The last beat is therefore a held `ready`, and the
 * budget it is held for is the one the board already models: the playout
 * ceiling the putt rings wait out. Past it the clip is demonstrably under way
 * and the slot goes quiet — which is also what keeps a player who arrives
 * mid-clip from being shown a countdown to something that happened.
 */
export const TEE_OFF_READY_HOLD_MS = BIRDIE_RESULT_HOLD_MS

const finite = (value: number | undefined): value is number =>
  value !== undefined && Number.isFinite(value)

export function teeOffCountdown(input: TeeOffInput): TeeOffBeat | undefined {
  if (input.settled || input.firstPuttSeen) return undefined
  if (!finite(input.startedAtMs)) return undefined
  if (!finite(input.revealDelayS) || input.revealDelayS <= 0) return undefined

  const remainingMs = input.startedAtMs + input.revealDelayS * 1000 - input.nowMs
  if (remainingMs > (input.beats ?? TEE_OFF_BEATS) * 1000) return undefined
  if (remainingMs > 0) return Math.ceil(remainingMs / 1000)
  return remainingMs > -TEE_OFF_READY_HOLD_MS ? 'ready' : undefined
}
